import { QueryBuilderModel } from 'bones/components/molecules/QueryBuilder/types/model';
import { QueryBuilderCopy } from 'bones/components/molecules/QueryBuilder/types/schema';
import {
  ANY_CLASS_IRI,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import {
  DraftFilter,
  DraftTopExpression,
} from 'bones/utils/queryBuilder/types';
import { getInvalidInstanceSubjects } from 'bones/utils/queryBuilder/validation/getInvalidInstanceSubjects';
import { getValidVariables } from 'bones/utils/queryBuilder/validation/getValidVariables';

export const validateSubject = ({
  subject,
  filter,
  topExpression,
  subExpressionIdx,
  expressionIdx,
  filterIdx,
  validateVariable,
  validModel,
  validVariables,
  copy,
}: {
  subject: DraftFilter['subject'];
  filter: DraftFilter;
  topExpression: DraftTopExpression;
  subExpressionIdx: number;
  expressionIdx: number;
  filterIdx: number;
  validateVariable: (id?: string) => boolean;
  validModel?: QueryBuilderModel;
  validVariables?: Record<string, string[]>;
  copy: QueryBuilderCopy;
}): { isValid: boolean; tooltip: string } => {
  if (!subject) {
    return { isValid: false, tooltip: '' };
  }

  // must be in the provided model classes
  if (
    validModel &&
    subject.iri !== ANY_CLASS_IRI &&
    !validModel.classesByIri[subject.iri]
  ) {
    return { isValid: false, tooltip: copy.error.classModelUndefined };
  }

  const invalidInstanceVariables = getInvalidInstanceSubjects({
    topExpression,
    expressionIdx,
    subExpressionIdx,
    filterIdx,
  });
  const invalidInstanceIds = invalidInstanceVariables[subject.iri] || [];
  const isInvalidInstance = invalidInstanceIds.includes(subject.id);

  // must be any provided variable
  if (validVariables) {
    const validIds = validVariables[subject.iri] || [];
    const isValid = validIds.includes(subject.id);
    if (!isValid) {
      return { isValid: false, tooltip: copy.error.classPreviousInstance };
    }

    if (isInvalidInstance) {
      return { isValid: false, tooltip: copy.error.classInstanceDefined };
    }

    return { isValid: true, tooltip: '' };
  }

  if (isInvalidInstance) {
    return { isValid: false, tooltip: copy.error.classInstanceDefined };
  }

  if (
    !validateVariable(subject.id) &&
    filter.operator?.operatorType &&
    // instance property
    (filter.operator.operatorType === OperatorType.INSTANCE ||
      // datatype property
      (filter.operator.operatorType === OperatorType.DATATYPE &&
        !filter.operator.comparison) ||
      // datatype comparison property with comparison subject
      (filter.operator.operatorType === OperatorType.DATATYPE &&
        filter.operator.comparison &&
        filter.comparisonSubject) ||
      // object property with value
      (filter.operator.operatorType === OperatorType.OBJECT && filter.value))
  ) {
    return { isValid: false, tooltip: copy.error.classSubjectConnected };
  }

  const validSubjects = getValidVariables({
    topExpression,
    expressionIdx,
    subExpressionIdx,
    filterIdx,
  });
  const validIds = validSubjects[subject.iri] || [];
  const isValid = validIds.includes(subject.id);
  return {
    isValid,
    tooltip: isValid ? '' : copy.error.classVariableInvalid,
  };
};
